import { useState } from 'react';
import { get } from 'lodash';
import {
  Col,
  ENotificationType,
  Modal,
  useForm,
  useShowToastNotification,
  useTranslation,
} from '@packages/utils';
import { TValidateFieldsError } from 'data/types/generic.types';
import { useTheme } from 'styled-components';
import { EIconNames, Icon } from 'components/Base/Icon';
import { useEditMeetingMutation } from 'redux/apiSlice/elcs.slice';
import { TEditMeetingFormProps, TEditMeetingProps } from './EditMeeting.types';
import AddEditMeetingForm from '../AddEditMeetingForm';

const EditMeeting = ({
  elcId,
  meetingId,
  initialValues,
  disabled = false,
}: TEditMeetingProps) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.elcs.detail.meetings-tab.list.actions.edit',
  });
  const theme = useTheme();
  const { showToastNotification } = useShowToastNotification();
  const { form } = useForm<TEditMeetingFormProps>();
  const [open, setOpen] = useState<boolean>(false);
  const [editMeeting, { isLoading }] = useEditMeetingMutation();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {
        meetingId,
        ...values,
      };

      if (elcId) {
        payload.elcId = elcId;
      }

      await editMeeting(payload).unwrap();
      showToastNotification({
        message: t('modal.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (err) {
      if (!(err as TValidateFieldsError)?.errorFields) {
        const message = get(err, 'data.message');
        showToastNotification({
          message,
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  return (
    <>
      <Col onClick={disabled ? undefined : handleOpen}>
        <Icon
          icon={EIconNames.EDIT_OUTLINED}
          color={disabled ? '#B5B5B5' : theme.colorBlackIconColor}
          className={disabled ? '' : 'pointer'}
          tooltip={{
            title: disabled
              ? 'Global meetings can only be modified from the All ELCs page'
              : t('EDIT_TOOLTIP'),
          }}
        />
      </Col>
      {!disabled && (
        <Modal
          title={t('modal.TITLE')}
          open={open}
          onCancel={handleClose}
          onOk={handleSubmit}
          centeredPosition
          okText={t('modal.SUBMIT_BTN')}
          cancelText={t('modal.CANCEL_BTN')}
          confirmLoading={isLoading}
          destroyOnClose
        >
          <AddEditMeetingForm
            form={form}
            initialValues={initialValues}
            open={open}
            isEditMode
          />
        </Modal>
      )}
    </>
  );
};

export default EditMeeting;
